import {
  AloftArea,
  AloftPrice,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftArea,
  schAloftPrice,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getComparableDisplayName, getScopedAlertType } from '../utils/compTypeScoping.ts';
import { getSubjectDataForCompType } from '../utils/getSubjectDataForCompType.ts';

const TARGET_CELL_TOOLTIP = 'Adjustment is disproportionate';
const TIPS =
  'Adjustment amounts should scale logically with the physical size variance, or provide detailed commentary as to the market data indications for a varying adjustment.';

const SITE_SIZE_DIFF_THRESHOLD = 0.5;

const getRequiredAdjustmentPct = (pctDiff: number): number => {
  if (pctDiff >= 2) return 0.02;
  if (pctDiff >= 1) return 0.01;
  return 0.005;
};

const getSiteSizePctDiff = (subjectSite: AloftArea, compSite: AloftArea): number | undefined => {
  const subjectSqft = subjectSite.toSqFt().unitAmount;
  if (subjectSqft === 0) return undefined;
  const compSqft = compSite.toSqFt().unitAmount;
  return Math.abs(compSqft - subjectSqft) / subjectSqft;
};

export const disproportionateSiteAdjustment: AlertDefinition = ({
  compAdjustments,
  compSequence,
  fullCompSequence,
  compType,
  order,
  propertyDataEntities,
}) => {
  const subjectData = (propertyDataEntities.subject as SubjectPropertyDataState | undefined)
    ?.subjectData.editableData;
  if (!subjectData) return [];

  const scopedSubjectData = getSubjectDataForCompType(subjectData, compType);
  const subjectSite = orUndefined(schAloftArea.parse)(scopedSubjectData.site?.area);
  if (!subjectSite || subjectSite.equals(AloftArea.zero)) return [];

  const fixRoute =
    schPairedCostRouteItem.enum[order.isInspectifyOrder ? 'adjustments-grid' : 'adjustments'];
  const triggers = getAlertTriggers({ fixRoute, order });

  return compSequence.flatMap((id) => {
    const propertyData = (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData
      .editableData;
    const compAdjustmentData = compAdjustments[id];
    if (!propertyData || !compAdjustmentData) return [];

    const compSite = orUndefined(schAloftArea.parse)(propertyData.site?.area);
    if (!compSite || compSite.equals(AloftArea.zero)) return [];

    const pctDiff = getSiteSizePctDiff(subjectSite, compSite);
    if (!isDefined(pctDiff) || pctDiff <= SITE_SIZE_DIFF_THRESHOLD) return [];

    const siteAdj = orUndefined(schAloftPrice.parse)(compAdjustmentData.site?.size?.adjustment);
    const adjustedSalePrice = orUndefined(schAloftPrice.parse)(
      compAdjustmentData.adjustedSalePrice?.adjustment,
    );
    if (!adjustedSalePrice || adjustedSalePrice.equals(AloftPrice.zero)) return [];

    const actualPct = siteAdj ? Math.abs(siteAdj.ratio(adjustedSalePrice) ?? 0) : 0;
    const requiredPct = getRequiredAdjustmentPct(pctDiff);
    if (actualPct >= requiredPct) return [];

    const propertyName = getComparableDisplayName(id, fullCompSequence ?? compSequence);
    const targetCellKey = `${id}-site` as PropertyGridCellKey;

    return [
      {
        type: getScopedAlertType(schAlertType.enum.disproportionateSiteAdjustment, compType),
        status: 'active' as const,
        severity: 'error' as const,
        orderType: ['appraisify'] as const,
        message: `${propertyName} Site Size adjustment appears disproportionate to the actual size difference`,
        propertyID: id,
        ...(compType ? { compType } : {}),
        fixingInfo: {
          tips: TIPS,
          pairedCostRouteItem: fixRoute,
          fixingMethod: ['action', 'comment'] as const,
          commentAlwaysRequired: false,
          targetCellKey,
          targetCellTooltip: TARGET_CELL_TOOLTIP,
        },
        triggers,
      },
    ];
  });
};
